import { lazy } from 'react';

/**
 * A wrapper around React.lazy() that retries failed chunk loads.
 *
 * When a new build is deployed, old chunk filenames become invalid (404).
 * This utility:
 *  1. Retries the dynamic import up to `maxRetries` times with exponential backoff
 *  2. On final failure, forces a page reload (with a sessionStorage guard to prevent loops)
 *
 * @param {() => Promise} importFunction - The dynamic import function, e.g. () => import('./MyComponent')
 * @param {number} [maxRetries=3] - Maximum number of retry attempts
 * @returns {React.LazyExoticComponent} A lazy-loaded component with retry logic
 */
const lazyWithRetry = (importFunction, maxRetries = 3) => {
  return lazy(() => retryImport(importFunction, maxRetries));
};

/**
 * Attempts to dynamically import a module, retrying on chunk load errors.
 * Falls back to a hard page reload as a last resort.
 */
const retryImport = (importFunction, retriesLeft, delay = 1000) => {
  return importFunction().catch((error) => {
    if (!isChunkLoadError(error)) {
      // Not a chunk load error — don't retry, just throw
      throw error;
    }

    if (retriesLeft > 0) {
      // Wait and retry with exponential backoff
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(retryImport(importFunction, retriesLeft - 1, delay * 2));
        }, delay);
      });
    }

    // All retries exhausted — force a page reload as last resort
    // Use sessionStorage guard to prevent infinite reload loops
    const reloadKey = 'chunk_reload_' + window.location.pathname;
    const hasReloaded = sessionStorage.getItem(reloadKey);

    if (!hasReloaded) {
      sessionStorage.setItem(reloadKey, 'true');
      window.location.reload();
      // Return a never-resolving promise to prevent rendering while reloading
      return new Promise(() => {});
    }

    // Already reloaded once and still failing — clear flag and throw
    sessionStorage.removeItem(reloadKey);
    throw error;
  });
};

/**
 * Checks whether an error is a chunk/module load failure.
 */
const isChunkLoadError = (error) => {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Loading CSS chunk') ||
    error?.message?.includes('Failed to fetch dynamically imported module')
  );
};

export default lazyWithRetry;
export { isChunkLoadError };
